// @flow
/**
 * The main interface for editing a component (widget). This is
 * essentially a UI for ultimately creating a props object.
 */
import type { $Widget } from '~/app/types';
import React from 'react';
import injectSheet from 'react-jss';
import classNames from 'classnames';
import _ from 'shared/lib/gettext';
import WidgetLoadables from 'shared/widgets';
import Button from '~/app/ui/Button';
import Modal from '~/app/components/Modal';
import EditComponentProp from './EditComponentProp';
import ErrorBoundary from 'shared/components/Error/ErrorBoundary';
import { enrichPropsSchemaItems } from 'shared/socialIconEnumOptions';
import { LATEST, getVersionOptions, getVersionStatus } from './customWidgetVersions';
import EditComponentContext from './editComponentContext';
import FieldsPanel from './aiWidget/FieldsPanel';
import { S, A, TONES, DARK, labelStyle } from './shell/shellTokens';

// Editors too big for the inspector column; they open in the full editor.
const FULL_EDITOR_TYPES = ['AI_CODE_GENERATOR', 'FLEXIBLE_COLUMN_LAYOUT', 'WATERFALL_GALLERY', 'HTML', 'LIVESTREAM_PLAYLIST', 'LIVESTREAM_CONTROL'];

type Props = {
  instance: {
    props: Object,
    widgetId: string,
    presetId: string,
    subcomponents: Array<Object>
  },
  schema: $Widget,
  classes: Object,
  onChange: (nextComponent: Object) => void,
  onClose: () => void,
  onContinue: () => void,
  onDelete?: () => void,
  isBasicUser: boolean,
  /** 'panel': the inspector column. Edits go out as they happen (onLiveChange); no Save. */
  variant?: 'modal' | 'panel',
  onLiveChange?: (nextComponent: Object, key: string) => void,
  onOpenFull?: () => void,
  presetTitle?: string
};

class EditComponent extends React.Component {
  props: Props = this.props;

  constructor(props) {
    super(props);
    this.state = {
      hiddenIndices: [],
      indexActiveTab: 0,
      // Copy current props into state. Updates will be applied here until the
      // user clicks 'save'.
      props: { ...props.instance.props },
      component: null,
      customWidgetSchema: null,
      availableVersions: [], // List of available versions for custom widgets
      selectedVersion: props.instance.props?.customWidgetVersion || null
    };
  }

  componentDidMount() {
    const { instance } = this.props;

    // LoadableComponent promise must be loaded
    if (instance && instance.widgetId) {
      const component = WidgetLoadables[instance.widgetId];
      // Check if it is a loadable or plain component
      if (Object.prototype.hasOwnProperty.call(component, 'load')) {
        component.load().then((w) => {
          this.setState({ component: w.default });
        });
      } else {
        this.setState({ component });
      }

      // For CustomWidget, fetch the actual widget schema and available versions
      if (instance.widgetId === 'CustomWidget' && instance.props.customWidgetName) {
        this.fetchCustomWidgetVersions(instance.props.customWidgetName);
        this.fetchCustomWidgetSchema(
          instance.props.customWidgetName,
          instance.props.customWidgetVersion
        );
      }
    }
  }

  /**
   * Fetch available versions for a custom widget
   */
  fetchCustomWidgetVersions = async (widgetName) => {
    try {
      const response = await fetch('/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: `
            query GetCustomWidgetVersions($name: String!) {
              customWidgetByName(name: $name) {
                publishedVersion
                versions {
                  versionId
                  createdAt
                }
              }
            }
          `,
          variables: { name: widgetName }
        })
      });

      const result = await response.json();
      
      if (result.data?.customWidgetByName) {
        const widget = result.data.customWidgetByName;
        this.setState({
          availableVersions: widget.versions || [],
          publishedVersion: widget.publishedVersion
        });
      }
    } catch (err) {
      console.error('Failed to fetch custom widget versions:', err);
    }
  }

  /**
   * Fetch the custom widget's propsSchema from the database.
   * Fills only missing prop keys from the widget's defaultProps — never overwrites
   * values the page instance already has saved.
   */
  fetchCustomWidgetSchema = async (widgetName, version) => {
    try {
      const response = await fetch('/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: `
            query GetCustomWidgetForRender($name: String!, $version: String) {
              customWidgetForRender(name: $name, version: $version) {
                propsSchema {
                  key
                  label
                  type
                  defaultValue
                  options {
                    label
                    value
                  }
                  children {
                    key
                    label
                    type
                    defaultValue
                    options {
                      label
                      value
                    }
                    children {
                      key
                      label
                      type
                      defaultValue
                      options {
                        label
                        value
                      }
                    }
                  }
                }
                defaultProps
              }
            }
          `,
          variables: {
            name: widgetName,
            version: version || undefined
          }
        })
      });

      const result = await response.json();

      if (result.data?.customWidgetForRender?.propsSchema) {
        const raw = result.data.customWidgetForRender;
        const customSchema = {
          ...raw,
          propsSchema: enrichPropsSchemaItems(raw.propsSchema),
        };
        const widgetDefaults = raw.defaultProps || {};

        this.setState((prevState) => {
          const currentProps = prevState.props || {};
          const nextProps = { ...currentProps };

          // Some instances store widget values under widgetProps (legacy / browser default)
          const widgetPropsBag = currentProps.widgetProps;
          if (widgetPropsBag && typeof widgetPropsBag === 'object' && !Array.isArray(widgetPropsBag)) {
            Object.keys(widgetPropsBag).forEach((key) => {
              if (nextProps[key] === undefined) {
                nextProps[key] = widgetPropsBag[key];
              }
            });
          }

          Object.keys(widgetDefaults).forEach((key) => {
            if (nextProps[key] === undefined) {
              nextProps[key] = widgetDefaults[key];
            }
          });

          customSchema.propsSchema.forEach((prop) => {
            if (nextProps[prop.key] === undefined && prop.defaultValue !== undefined) {
              nextProps[prop.key] = prop.defaultValue;
            }
          });

          if (version) {
            nextProps.customWidgetVersion = version;
          }

          return { customWidgetSchema: customSchema, props: nextProps };
        });
      }
    } catch (err) {
      console.error('Failed to fetch custom widget schema:', err);
    }
  }

  collapseAll = () => {
    const schema = this.getEffectiveSchema();
    if (!schema || !schema.propsSchema) return;
    
    const activePropsSchema = schema.propsSchema[this.state.indexActiveTab];
    if (!activePropsSchema) return;
    
    const value = this.state.props[activePropsSchema.key];

    if (Array.isArray(value)) {
      this.setState({ hiddenIndices: value.map((x, i) => i) });
    }
  }

  expandAll = () => {
    this.setState({ hiddenIndices: [] });
  }

  // Prefer continuing the flow; otherwise, just close
  handleNextStep = () => {
    const { onContinue, onClose } = this.props;

    if (onContinue) {
      onContinue();
    } else {
      onClose();
    }
  }

  // Only the page editor lets a property editor replace the whole component.
  replaceContext = {
    replace: this.props.allowReplace
      ? (component) => {
        this.props.onChange(component);
        this.handleNextStep();
      }
      : null
  };

  // The inspector column: the same replace, and compact field editors.
  panelContext = { ...this.replaceContext, compact: true };

  // The context with the section's id (its editor-session id until the draft is saved),
  // kept as the same object while the section stays the same so fields don't re-render.
  contextFor = (base, componentId) => {
    this.contextCache = this.contextCache || new Map();
    const key = `${base === this.panelContext ? 'panel' : 'modal'}:${componentId || ''}`;
    if (!this.contextCache.has(key)) this.contextCache.set(key, { ...base, componentId: componentId || null });
    return this.contextCache.get(key);
  };

  handleSave = () => {
    const { instance, onChange } = this.props;
    const { props } = this.state;

    if (instance.presetId) {
      alert(_('EDIT_NOT_ALLOWED'));
    } else {
      onChange({
        props,
        widgetId: instance.widgetId,
        subcomponents: instance.subcomponents
      });
    }
    this.handleNextStep();
  }

  handleChange(key, val) {
    const newProps = {
      ...this.state.props,
      [key]: val
    };

    this.setState({ props: newProps });
    this.emitLive(newProps, key);
  }

  // In the inspector, every change goes straight out (the host debounces it).
  emitLive = (props, key) => {
    const { variant, onLiveChange, instance } = this.props;
    if (variant !== 'panel' || !onLiveChange || instance.presetId) return;
    onLiveChange({ props, widgetId: instance.widgetId, subcomponents: instance.subcomponents }, key);
  }

  /**
   * Handle version change for custom widgets
   */
  handleVersionChange = (newVersion) => {
    const { instance } = this.props;

    const props = {
      ...this.state.props,
      customWidgetVersion: newVersion,
    };
    this.setState({
      selectedVersion: newVersion,
      props,
      customWidgetSchema: null
    });
    this.emitLive(props, 'customWidgetVersion');

    this.fetchCustomWidgetSchema(
      instance.props.customWidgetName,
      newVersion
    );
  }

  renderMenuItem(schema, i) {
    const { classes } = this.props;

    return (
      <div
        className={classNames(classes.menuItem, {
          'is-active': this.state.indexActiveTab === i
        })}
        key={schema.key}
        onClick={() => this.setState({ hiddenIndices: [], indexActiveTab: i })}
      >
        {schema.label}
      </div>
    );
  }

  /**
   * Get the effective schema, merging custom widget schema if applicable
   */
  getEffectiveSchema = () => {
    const { instance } = this.props;
    const { component, customWidgetSchema } = this.state;

    if (!component) return null;

    // For CustomWidget, build a schema that includes the custom widget's props
    if (instance.widgetId === 'CustomWidget' && customWidgetSchema) {
      const customPropsSchema = customWidgetSchema.propsSchema || [];
      // Widgets without props fall back to the base schema. Its raw "Version" text
      // field edits the same prop as the version dropdown in the footer without
      // updating it, so leave it out; the dropdown is always shown for custom widgets.
      const fallbackPropsSchema = component.schema.propsSchema.filter(p => p.key !== 'customWidgetVersion');

      return {
        ...component.schema,
        title: `${instance.props.customWidgetName || 'Custom Widget'}`,
        propsSchema: customPropsSchema.length > 0 ? customPropsSchema : fallbackPropsSchema
      };
    }

    return component.schema;
  }

  /** The inspector column: the same fields as the edit window, applied as you type. */
  renderPanel() {
    const { classes, instance, onOpenFull, presetTitle } = this.props;
    const { props, indexActiveTab, customWidgetSchema, availableVersions, selectedVersion, publishedVersion } = this.state;
    const isCustomWidget = instance.widgetId === 'CustomWidget';
    const schema = this.getEffectiveSchema();
    const title = (schema && schema.title) || instance.widgetId;
    const versionStatus = getVersionStatus(selectedVersion, publishedVersion);

    const head = (
      <div className={classes.panelHead}>
        <div className={classes.panelTitle} title={title}>{title}</div>
        {isCustomWidget && (
          <label className={classes.panelVersion}>
            <span>Version</span>
            <select value={selectedVersion || LATEST} onChange={e => this.handleVersionChange(e.target.value)} disabled={!!instance.presetId}>
              {getVersionOptions(availableVersions, publishedVersion, selectedVersion).map(o => (
                <option key={o.value || 'latest'} value={o.value}>{o.label}</option>
              ))}
            </select>
            {versionStatus && <em className={versionStatus.tone === 'warn' ? classes.panelWarn : classes.panelInfo}>{versionStatus.text}</em>}
          </label>
        )}
      </div>
    );
    const foot = (
      <div className={classes.panelFoot}>
        {/* Deleting is in the section's ⋯ menu, away from the buttons used all the time. */}
        {onOpenFull && <button type="button" className={classes.panelButton} onClick={onOpenFull}>Open full editor</button>}
      </div>
    );

    let body;
    if (instance.presetId) {
      body = (
        <p className={classes.panelNote}>
          This section uses the preset {presetTitle ? `“${presetTitle}”` : ''}, so its content comes from the preset.
          Edit the preset under Widget Presets, or choose “Use presets” on the section to detach it.
        </p>
      );
    } else if (isCustomWidget && instance.props.customWidgetName && !customWidgetSchema) {
      body = <div className={classes.panelNote}>Loading widget properties…</div>;
    } else if (!schema || !schema.propsSchema || !schema.propsSchema.length) {
      body = <div className={classes.panelNote}>This widget has no editable properties.</div>;
    } else if (instance.widgetId === 'AiGeneratedWidget') {
      const content = props.content || {};
      body = (
        <div className={classes.panelFields}>
          <FieldsPanel html={content.html || ''} onChange={html => this.handleChange('content', { ...content, html })} />
          <p className={classes.panelNote}>Paste new code, see the checks or edit the code in the full editor.</p>
        </div>
      );
    } else {
      body = schema.propsSchema.map((item, i) => {
        const open = indexActiveTab === i;
        return (
          <section key={item.key} className={classes.panelSection}>
            <button
              type="button"
              className={classes.panelSectionTitle}
              aria-expanded={open}
              onClick={() => this.setState({ hiddenIndices: [], indexActiveTab: open ? -1 : i })}
            >
              <span>{item.label}</span>
              <i className="material-icons" aria-hidden>{open ? 'expand_less' : 'expand_more'}</i>
            </button>
            {open && (FULL_EDITOR_TYPES.includes(item.type)
              ? <p className={classes.panelNote}>This field needs more room. {onOpenFull && <button type="button" className={classes.panelLink} onClick={onOpenFull}>Open the full editor</button>}</p>
              : (
                <div className={classes.panelBody}>
                  <EditComponentProp
                    root
                    hiddenIndices={this.state.hiddenIndices}
                    schema={item}
                    value={props[item.key]}
                    onChange={(key, newValue) => this.handleChange(key, newValue)}
                  />
                </div>
              ))}
          </section>
        );
      });
    }

    return (
      <div className={classes.panel} data-testid="section-inspector" data-widget={instance.widgetId}>
        {head}
        <ErrorBoundary isEditor>
          <EditComponentContext.Provider value={this.contextFor(this.panelContext, instance && instance._id)}>
            {body}
          </EditComponentContext.Provider>
        </ErrorBoundary>
        {foot}
      </div>
    );
  }

  render() {
    const { onDelete, onClose, isBasicUser } = this.props;
    const { classes } = this.props;
    const { props, indexActiveTab, component, customWidgetSchema, availableVersions, selectedVersion, publishedVersion } = this.state;
    const { instance } = this.props;
    const isCustomWidget = instance.widgetId === 'CustomWidget';

    const isPanel = this.props.variant === 'panel';

    // For CustomWidget, wait for both component and custom schema to load
    if (!component) {
      return isPanel ? <div className={classes.panelNote}>Loading…</div> : null;
    }

    if (isPanel) {
      return this.renderPanel();
    }

    if (instance.widgetId === 'CustomWidget' && instance.props.customWidgetName && !customWidgetSchema) {
      return (
        <Modal title="Loading..." onClose={onClose}>
          <div style={{ padding: '40px', textAlign: 'center' }}>
            Loading widget properties...
          </div>
        </Modal>
      );
    }

    const schema = this.getEffectiveSchema();
    
    if (!schema || !schema.propsSchema || schema.propsSchema.length === 0) {
      return (
        <Modal title="No Properties" onClose={onClose}>
          <div style={{ padding: '40px', textAlign: 'center' }}>
            This widget has no editable properties.
          </div>
        </Modal>
      );
    }

    const activePropsSchema = schema.propsSchema[indexActiveTab];
    
    // Version selector for custom widgets (in footer)
    const versionStatus = getVersionStatus(selectedVersion, publishedVersion);
    const versionSelector = isCustomWidget && (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <label className={classes.versionLabel} style={{ 
          fontWeight: 500, 
          fontSize: '12px'
        }}>
          Version:
        </label>
        <select
          value={selectedVersion || LATEST}
          onChange={(e) => this.handleVersionChange(e.target.value)}
          className={classes.versionSelect}
          style={{
            padding: '5px 10px',
            borderRadius: '3px',
            fontSize: '12px',
            cursor: 'pointer'
          }}
        >
          {getVersionOptions(availableVersions, publishedVersion, selectedVersion).map(o => (
            <option key={o.value || 'latest'} value={o.value}>{o.label}</option>
          ))}
        </select>
        {versionStatus && (
          <span className={versionStatus.tone === 'warn' ? classes.versionWarn : classes.versionInfo} style={{
            fontSize: '10px',
            padding: '3px 6px',
            borderRadius: '3px',
            fontWeight: 500
          }}>
            {versionStatus.text}
          </span>
        )}
      </div>
    );

    const footer = (
      <div className={classes.footer}>
        <div className={classes.footerLeft}>
          {versionSelector}
          {
            !isBasicUser && !versionSelector && onDelete &&
            <Button variant="danger" onClick={onDelete}>Delete</Button>
          }
        </div>
        <div className={classes.footerRight}>
          {
            !isBasicUser && versionSelector && onDelete &&
            <Button variant="danger" onClick={onDelete}>Delete</Button>
          }
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={this.handleSave}>Save</Button>
        </div>
      </div>
    );
    const menu = (
      <div className={classes.bodyLeft}>
        {schema.propsSchema.map((s, i) => this.renderMenuItem(s, i))}
      </div>
    );
    const content = (
      <div className={classes.bodyRight}>
        {
          activePropsSchema.type === 'ARRAY' &&
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            marginBottom: '8px',
          }}>
            <div
              className={classes.foldButton}
              style={{
                borderRadius: '5px',
                padding: '3px',
                margin: '0 4px',
                cursor: 'pointer',
                userSelect: 'none',
              }}
              onClick={this.collapseAll}
            >
              <i title="Collapse all" className="material-icons">unfold_less</i>
            </div>
            <div
              className={classes.foldButton}
              style={{
                borderRadius: '5px',
                padding: '3px',
                margin: '0 4px',
                cursor: 'pointer',
                userSelect: 'none',
              }}
              onClick={this.expandAll}
            ><i title="Expand all" className="material-icons">unfold_more</i></div>
          </div>
        }
        <EditComponentProp
          root
          hiddenIndices={this.state.hiddenIndices}
          schema={activePropsSchema}
          value={props[activePropsSchema.key]}
          key={activePropsSchema.key} // Adds a unique key for each rendered component
          onChange={(key, newValue) => this.handleChange(key, newValue)}
        />
      </div>
    );

    return (
      <Modal
        title={`Edit ${schema.title}`}
        actions={footer}
        onClose={onClose}
      >
        <ErrorBoundary isEditor>
          <EditComponentContext.Provider value={this.contextFor(this.replaceContext, this.props.instance && this.props.instance._id)}>
            {menu}
            {content}
          </EditComponentContext.Provider>
        </ErrorBoundary>
      </Modal>
    );
  }
}

/* =============================================================================
=    CSS
============================================================================= */
const panelButtonBase = {
  all: 'unset',
  boxSizing: 'border-box',
  cursor: 'pointer',
  padding: '7px 12px',
  fontSize: 13,
  fontWeight: 600,
  borderRadius: 6,
  textAlign: 'center'
};

const styles = {
  // minmax(0, 1fr): the column's width wins over wide content (a long version name), which shrinks or wraps.
  panel: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 0, fontFamily: S.ui, color: S.fg, fontSize: 13 },
  panelHead: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 8, paddingBottom: 12 },
  panelTitle: { fontSize: 16, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  panelVersion: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 6,
    fontSize: 12,
    color: S.muted,
    '& select': { flex: '1 1 160px', minWidth: 0, maxWidth: '100%', textOverflow: 'ellipsis', fontFamily: S.mono, fontSize: 12, padding: '4px 6px', borderRadius: 6, border: `1px solid ${S.fieldLine}`, background: S.field, color: S.fg },
    '& em': { fontStyle: 'normal', fontSize: 11, fontWeight: 600, padding: '1px 6px', borderRadius: 4 }
  },
  panelWarn: { color: S.amber, background: '#FBF1DE', [DARK]: { background: TONES.amber.bg } },
  panelInfo: { color: S.fg, background: S.line },
  panelSection: { borderTop: `1px solid ${S.line}` },
  panelSectionTitle: {
    ...panelButtonBase,
    width: '100%',
    padding: '11px 0',
    borderRadius: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    ...labelStyle,
    textAlign: 'left',
    '& .material-icons': { fontSize: 18, flexShrink: 0 },
    '&:hover, &:focus-visible': { color: S.fg }
  },
  // Today's field editors were laid out for the wide edit window: keep them in the column.
  panelBody: {
    paddingBottom: 14,
    '& input, & textarea, & select': { maxWidth: '100%', boxSizing: 'border-box' },
    '& > *': { maxWidth: '100%' }
  },
  panelFields: { display: 'grid', gap: 12, paddingTop: 4 },
  panelNote: { margin: '6px 0 12px', fontSize: 13, lineHeight: 1.5, color: S.muted },
  panelLink: { ...panelButtonBase, padding: 0, fontSize: 13, textDecoration: 'underline', color: S.fg },
  panelFoot: { display: 'grid', gap: 8, paddingTop: 14, borderTop: `1px solid ${S.line}` },
  panelButton: { ...panelButtonBase, border: `1px solid ${S.fieldLine}`, background: S.field, color: S.fg, '&:hover, &:focus-visible': { borderColor: S.fg } },
  bg: {
    position: 'fixed',
    top: '49px',
    left: '0',
    bottom: '0',
    right: '0',
    background: 'hsla(0, 0%, 0%, 0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
    [DARK]: { background: S.scrim }
  },
  container: {
    width: '80%',
    height: '80%',
    background: A.surface,
    zIndex: 11,
    padding: '0',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between'
  },
  header: {
    flex: '0 0 50px',
    height: '50px',
    color: 'hsl(0, 0%, 90%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 15px',
    background: 'hsl(0, 0%, 10%)'
  },
  body: {
    flexGrow: '2',
    display: 'flex'
  },
  bodyLeft: {
    borderRight: '1px solid hsl(0, 0%, 90%)',
    width: '200px',
    minWidth: '200px',
    display: 'flex',
    flexDirection: 'column',
    padding: '0',
    background: 'hsl(220, 0%, 95%)',
    overflowY: 'auto',
    [DARK]: { borderRightColor: S.line, background: S.chrome }
  },
  menuItem: {
    color: 'hsl(0, 0%, 30%)',
    cursor: 'pointer',
    padding: '15px 10px',
    textAlign: 'left',
    fontWeight: '300',
    background: 'none',
    '&:hover': {
      background: 'hsl(225, 5%, 90%)',
    },
    '&.is-active': {
      color: 'hsl(200, 41%, 33%)',
      fontWeight: '400',
      background: 'hsl(225, 5%, 90%)',
    },
    [DARK]: {
      color: S.fg,
      '&:hover': { background: S.chromeHover },
      '&.is-active': { color: TONES.blue.fg, background: A.rowSelected }
    }
  },
  bodyRight: {
    padding: '20px',
    flexGrow: 2,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'scroll',
    backgroundColor: 'rgb(250,250,250)',
    [DARK]: { backgroundColor: A.surface }
  },
  close: {
    fontSize: '15px',
    color: 'hsl(0, 0%, 30%)',
    cursor: 'pointer',
    [DARK]: { color: S.muted }
  },
  footer: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 15px',
    borderTop: '1px solid hsl(0, 0%, 90%)',
    [DARK]: { borderTopColor: S.line }
  },
  footerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },
  // The version picker and the collapse/expand buttons in the edit window.
  versionLabel: { color: '#374151', [DARK]: { color: S.fg } },
  versionSelect: { border: '1px solid #d1d5db', background: S.field, [DARK]: { borderColor: S.fieldLine, color: S.fg } },
  versionWarn: { color: '#d97706', background: '#fef3c7', [DARK]: { color: TONES.amber.fg, background: TONES.amber.bg } },
  versionInfo: { color: '#2563eb', background: '#dbeafe', [DARK]: { color: TONES.blue.fg, background: TONES.blue.bg } },
  foldButton: { border: '1px solid #e6e6e6', color: '#666666', [DARK]: { borderColor: S.line, color: S.muted } }
};

export default injectSheet(styles)(EditComponent);
